'use client';
import { useCallback, useEffect, useState } from 'react';
import { statusWidth } from './StatusFlap';

/* ============================================================
   useBoardScale + useFullscreen — the TV presentation layer (§5.5).

   Given the viewport, pick the largest font size whose row still fits,
   clamped to a sane range. That's the whole "layout tier" problem — one
   number, derived, not a pile of breakpoints.

   Row width is ADDED UP from the real component geometry (rowWidth below),
   never estimated from a multiplier. Two successive multiplier models
   (`size * 44`, then `n * size * 0.78`) both under-measured and both
   clipped the right edge; see the comment on rowWidth.

   Three tiers fall out of it naturally:
     compact (< 900px)  size ~14-20, header shrinks, labels drop
     desktop (< 1600px) size ~20-28
     tv      (>= 1600px) size 28-44, generous row spacing

   We also scale by ROW COUNT: a 12-team league is 6 rows, a 14-team is 7;
   at some point vertical room, not width, is the binding constraint.
   ============================================================ */

const ROW_HEIGHT_FACTOR = 1.9; // matchup row height as a multiple of size
const MIN_SIZE = 12;
const MAX_SIZE = 44;

// Standings gets its own ceiling. 44 is a MATCHUP-row limit -- past it two
// teams plus a status block stop fitting side by side. A standings row is
// one team with no status flap, so on a 1440p or 4K panel it hits 44 with
// hundreds of px to spare and just looks small. 64 is the point where a
// 10-team board fills a 4K panel vertically; beyond that the flaps get
// comically large relative to the header.
const MAX_STANDINGS_SIZE = 64;

// StandingsBoard geometry (StandingsBoard.js):
//   StandingRow height = round(size * 1.5), column gap 12, 4 gaps
//   board gap between rows = 6
//   header label row = ~size*0.34 tall + 4px margin
const STANDINGS_ROW_H = 1.5;
const STANDINGS_ROW_GAP = 6;
const STANDINGS_COL_GAP = 12;

// TRUE SplitFlap geometry -- mirrors SplitFlap.js exactly:
//   cell width : 0.72 * size   (the inline style on Cell)
//   gap        : 3px           (.sf-row gap)
//   padding    : 10px per side (.sf-row padding)
//
// IF YOU CHANGE .sf-row's gap OR padding, OR Cell's 0.72, CHANGE THESE.
// These three numbers and SplitFlap's are the same fact written twice; the
// only reason they aren't imported is that SplitFlap's live in a CSS
// string, not in exported constants.
const SF_CELL_W = 0.72;
const SF_GAP = 3;
const SF_PAD = 10;

// Rendered width of one SplitFlap run of `cells` cells, in px.
//
// The gap is BETWEEN cells (n-1 of them) and the padding is a flat 20px on
// the run as a whole -- neither scales with font size. That is the entire
// bug this replaced: see rowWidth.
export function flapWidth(cells, size) {
  return cells * (SF_CELL_W * size) + (cells - 1) * SF_GAP + SF_PAD * 2;
}

// Measured width of one matchup row, in px, at a given size:
//   [medal] [avatar rank name pts] vs [avatar rank name pts] [status]
//
// WHY THIS REPLACED `6 + n * size * 0.78`:
//
// That model folded SplitFlap's fixed 3px inter-cell gap into the
// font-size multiplier, and approximated the 20px run padding as 6. But
// gap and padding are FIXED PIXELS -- they do not scale with size. So the
// error was near-constant (~120-170px per row), not proportional: worst at
// small sizes, and still 116px short even at size 44.
//
// A constant error is the dangerous kind here, because computeScale walks
// DOWN through candidate sizes looking for the first that fits. Every
// candidate was under-measured by roughly the same amount, so the walk
// overshot at every viewport it bound on, and the rightmost element -- the
// status flip-dot -- ran off the edge in fullscreen. It also explains why
// lowering StatusFlap.DOT_MAX appeared to do nothing: the 49px it frees is
// smaller than the error, so the chosen size rarely moved.
//
// The status block is genuinely non-linear too (it steps in discrete dot
// sizes and is capped), which is why statusWidth() is called rather than
// modelled -- that part of the original reasoning was right.
//
// EVERY term below is a real measurement. If you add anything to a matchup
// row, add it here: this function is the single source of truth for how
// wide a row is.
export function rowWidth(size, nameCells, { playoff = false } = {}) {
  const team =
    size +                            // avatar (AvatarFlap is exactly `size`)
    6 + flapWidth(2, size) +          // rank, 2 cells
    6 + flapWidth(nameCells, size) +  // team name
    6 + flapWidth(3, size);           // points, 3 cells
  const vs = size * 0.55 * 2;         // the "vs" glyph
  const medal = playoff ? size : 0;   // one emoji slot, playoffs only
  const gaps = 16 * 3 + 8 + (playoff ? 16 : 0);  // MatchupRow flex gaps
  return medal + team * 2 + vs + statusWidth(size) + gaps;
}

// Horizontal gutter each side, in px. MUST match the shell padding in
// SolariBoard -- if the two disagree, the board is centered inside a
// budget that isn't the real one and drifts off to one side. Exported so
// SolariBoard can reference it rather than restating the number.
export const BOARD_PAD_X = 48;

// Measured width of one STANDINGS row, in px:
//   [rank2] [avatar] [name N] [record5] [pf5] [marker]
//
// This is a DIFFERENT row from a matchup row and needs its own function.
// Sizing standings by rowWidth() was clamping it against two teams and a
// status flip-dot that a standings row does not contain -- which is why
// the standings board rendered narrow with dead space to its right.
//
// The marker slot holds either the word CHAMPION (size*0.36, 8 chars,
// letter-spaced) or a single emoji. CHAMPION is the wider of the two, so
// budget for it -- otherwise the champion row is the one that overflows.
export function standingsWidth(size, nameCells) {
  const marker = 10 + size * 0.36 * 8 * 1.25;
  return (
    flapWidth(2, size) +          // rank
    size +                        // avatar
    flapWidth(nameCells, size) +  // team name
    flapWidth(5, size) +          // record, W-L
    flapWidth(5, size) +          // points for
    STANDINGS_COL_GAP * 4 +       // flex gaps between the five columns
    marker
  );
}

// Measured height of the whole standings table, in px, including the
// header label row. Height is the binding constraint here -- unlike
// matchups, standings row count grows with team count (a 12-team league is
// 6 matchup rows but 12 standings rows).
export function standingsHeight(size, teamCount, { showLabels = true } = {}) {
  const rows = teamCount * (Math.round(size * STANDINGS_ROW_H) + STANDINGS_ROW_GAP);
  const labels = showLabels ? size * 0.34 + 4 : 0;
  return rows + labels;
}

// Largest size at which `teamCount` standings rows fit the viewport.
//
// Solved by walking down like computeScale does, because neither
// constraint is linear: flapWidth carries fixed px terms and the row
// height is rounded per row.
export function computeStandingsScale(vw, vh, teamCount, opts = {}) {
  const { chromeH = 200, padX = BOARD_PAD_X, headerH = 0 } = opts;
  const usableW = Math.max(320, vw - padX * 2);
  const usableH = Math.max(240, vh - chromeH - headerH);
  const tier = vw >= 1600 ? 'tv' : vw >= 900 ? 'desktop' : 'compact';
  const showLabels = tier !== 'compact';

  // Name cells: standings has width to spare, so TV tiers can afford more
  // than the 24 the matchup-derived value used. Long fantasy team names
  // ("FELLOWSHIP OF THE TDS") truncate at 24 and fit at 28.
  let nameCells = tier === 'compact' ? 12 : tier === 'desktop' ? 20 : 28;

  const fits = (s, n) =>
    standingsWidth(s, n) <= usableW &&
    standingsHeight(s, teamCount, { showLabels }) <= usableH;

  let size = MIN_SIZE;
  let found = false;
  for (let s = MAX_STANDINGS_SIZE; s >= MIN_SIZE; s--) {
    if (fits(s, nameCells)) { size = s; found = true; break; }
  }

  // Nothing fit even at the floor: give width back by shortening names
  // before letting the table overflow. A truncated name is legible; a row
  // running off the edge is the bug we just spent a session fixing.
  if (!found) {
    while (nameCells > 8 && !fits(MIN_SIZE, nameCells)) nameCells -= 1;
    size = MIN_SIZE;
  }

  return { size, nameWidth: nameCells, showLabels, tier };
}

export function computeScale(vw, vh, rowCount, opts = {}) {
  const { chromeH = 160, padX = BOARD_PAD_X, playoff = false } = opts;
  const usableW = Math.max(320, vw - padX * 2);
  const usableH = Math.max(240, vh - chromeH);

  const tier = vw >= 1600 ? 'tv' : vw >= 900 ? 'desktop' : 'compact';

  // Name cells are the one width term we can spend. Everything else on the
  // row is either fixed (status block, gaps) or already at MIN_SIZE.
  //
  // On a phone even a 10-cell name overflows at size 12 -- the row needs
  // ~768px and a 393px viewport has 320. Rather than let it clip, drop
  // name cells until the row fits at the floor. Truncating a team name is
  // a legible compromise; a status flap sliced in half is a broken board.
  // Desktop and TV never enter this loop.
  let nameCells = tier === 'compact' ? 10 : 14;
  while (nameCells > 4 && rowWidth(MIN_SIZE, nameCells, { playoff }) > usableW) {
    nameCells -= 1;
  }

  // Height is a clean linear constraint, so solve it directly.
  const byHeight = rowCount > 0
    ? usableH / (rowCount * ROW_HEIGHT_FACTOR)
    : MAX_SIZE;

  // Width isn't linear (see rowWidth), so walk down from the ceiling and
  // take the largest size that actually fits. At most 33 iterations of
  // trivial arithmetic, run once per resize -- cheaper than the reflow it
  // prevents, and it can't drift out of sync with the components the way a
  // hand-tuned constant does.
  let byWidth = MIN_SIZE;
  for (let s = Math.floor(Math.min(MAX_SIZE, byHeight)); s >= MIN_SIZE; s--) {
    if (rowWidth(s, nameCells, { playoff }) <= usableW) { byWidth = s; break; }
  }

  const size = Math.floor(Math.min(byWidth, byHeight, MAX_SIZE));
  const clamped = Math.max(MIN_SIZE, size);

  return {
    size: clamped,
    tier,
    playoff,
    // name cells: narrow screens can't afford 14 (§4 truncation is fine)
    nameWidth: nameCells,
    // DEPRECATED -- superseded by computeStandingsScale().nameWidth.
    //
    // Standings no longer inherits its width from the matchup scale, so
    // this value has no consumer. Kept only so an older test asserting on
    // it doesn't break; delete it once tests/fit.mjs is regenerated.
    standingsNameWidth: tier === 'compact' ? 12 : tier === 'desktop' ? 20 : 24,
    // MAX cells for the league name in the header. BoardHeader sizes to
    // content within this, so a short name doesn't reserve dead flaps.
    //
    // Raised from a flat 18. The header is the least dense line on the
    // board -- one name plus a 7-cell label against a matchup row's two
    // names, two ranks, two scores and a status. At size=41 on 1080p,
    // 28 name cells + gap + 7 week cells is ~890px against rows already
    // running past 1300px. Compact stays modest because it is the tier
    // that actually runs out of width.
    headerNameWidth: tier === 'compact' ? 20 : tier === 'desktop' ? 26 : 28,
    headerSize: Math.max(12, Math.round(clamped * (tier === 'tv' ? 0.9 : 0.8))),
    rowGap: Math.round(clamped * (tier === 'tv' ? 0.55 : 0.38)),
    dotSize: tier === 'tv' ? 5 : tier === 'desktop' ? 4 : 3,
    showLabels: tier !== 'compact',
  };
}

// Shared viewport tracker so the two scale hooks can't drift apart in how
// they listen for resizes.
function useViewport() {
  const [vp, setVp] = useState(() => ({
    w: typeof window === 'undefined' ? 1280 : window.innerWidth,
    h: typeof window === 'undefined' ? 800 : window.innerHeight,
  }));

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    let raf = 0;
    const on = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() =>
        setVp({ w: window.innerWidth, h: window.innerHeight })
      );
    };
    window.addEventListener('resize', on);
    return () => { window.removeEventListener('resize', on); cancelAnimationFrame(raf); };
  }, []);

  return vp;
}

export default function useBoardScale(rowCount = 5, { playoff = false } = {}) {
  const vp = useViewport();
  return computeScale(vp.w, vp.h, rowCount, { playoff });
}

// Standings sizes against ITS OWN row count and ITS OWN row shape.
//
// Previously SolariBoard passed `scale.size` -- derived from the MATCHUP
// row count -- to StandingsBoard. A 10-team league is 5 matchup rows, so
// the standings table was sized as though it had 5 rows to fill and used
// half the available height, while also being width-clamped by a status
// flip-dot it doesn't render. Both halves of that are fixed by measuring
// the real thing.
export function useStandingsScale(teamCount = 10, opts = {}) {
  const vp = useViewport();
  return computeStandingsScale(vp.w, vp.h, teamCount, opts);
}

export function useFullscreen(ref) {
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const on = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', on);
    return () => document.removeEventListener('fullscreenchange', on);
  }, []);

  const toggle = useCallback(async () => {
    if (typeof document === 'undefined') return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await (ref?.current || document.documentElement).requestFullscreen();
      }
    } catch { /* denied or unsupported — the board still works windowed */ }
  }, [ref]);

  // 'f' toggles fullscreen; Escape is handled by the browser
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onKey = (e) => {
      if (e.key === 'f' || e.key === 'F') {
        const tag = e.target?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;
        toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle]);

  return { isFullscreen, toggle };
}
